import styled from "@emotion/styled";
import CloseIcon from "@mui/icons-material/Close";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { PageWithFixedBottom } from "../../components/Templates/PageWithFixedBottom";
import { CollapsibleInvestmentProposal } from "../../components/Templates/UserDetail/ProposalMesage/CollapsibleInvestmentProposal";
import { BULK_MAX_TARGET_USER_COUNT } from "../../constants/BulkProposal";
import { useBulkConditionTarget } from "../../hooks/users/useBulkConditionTarget";
import { SearchFormTypeForProposedUserListState } from "../../view_models/atoms";

const StyledAccordion = styled(Accordion)`
  &.Mui-expanded {
    margin-top: 0;
  }
`;
const StyledAccordionSummary = styled(AccordionSummary)`
  padding: 0 2rem;
  background-color: #f5f5f5;
  height: 40px;
  min-height: 40px;
  font-weight: bold;
  &.Mui-expanded {
    min-height: 40px;
  }
`;

const InvestmentProposalBulkReRegister: React.FC = () => {
  const location = useLocation();
  const state = location.state as { userIds?: number[]; lastProposalDates?: Record<number, string | null> } | null;

  const searchForm = useRecoilValue(SearchFormTypeForProposedUserListState);
  const conditionTarget = useBulkConditionTarget({
    searchForm,
    isReProposal: true,
    listPath: "/users/proposed",
  });

  const userIds: number[] = useMemo(() => {
    if (!state?.userIds || state.userIds.length === 0) return [];
    const seen = new Set<number>();
    return state.userIds.filter((id) => {
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [state]);

  const initialLastProposalMap: Record<number, string | null> = useMemo(
    () => state?.lastProposalDates ?? {},
    [state]
  );

  const [lastProposalMap, setLastProposalMap] = useState<Record<number, string | null>>(initialLastProposalMap);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>(userIds);

  const formatLastProposalText = (userId: number) => {
    if (!Object.prototype.hasOwnProperty.call(lastProposalMap, userId)) {
      return "-";
    }

    const lastProposalDate = lastProposalMap[userId];

    if (!lastProposalDate) {
      return "前回提案なし";
    }

    return dayjs(lastProposalDate).format("YYYY/MM/DD HH:mm");
  };

  const isSameDayProposal = (userId: number) => {
    const lastProposalDate = lastProposalMap[userId];
    if (!lastProposalDate) return false;
    return dayjs(lastProposalDate).isSame(dayjs(), "day");
  };

  const handleRemoveUserId = (userId: number) => {
    setSelectedUserIds((prev) => prev.filter((id) => id !== userId));
    setLastProposalMap((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, userId)) {
        return prev;
      }
      const { [userId]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const eligibleUserIds = useMemo(
    () => selectedUserIds.filter((userId) => !isSameDayProposal(userId)),
    [selectedUserIds, lastProposalMap]
  );

  const excludedTodayUserIds = useMemo(
    () => selectedUserIds.filter((userId) => isSameDayProposal(userId)),
    [selectedUserIds, lastProposalMap]
  );

  // 条件が復元できない / 件数が取れない場合はフックが一覧へ戻すため、ここでは何も描画しない
  if (conditionTarget.isConditionMode && conditionTarget.status !== "ready") {
    return null;
  }

  if (conditionTarget.isConditionMode && conditionTarget.search) {
    return (
      <PageWithFixedBottom
        bottomContent={
          <CollapsibleInvestmentProposal
            isBulkReProposal={true}
            conditionTarget={{ search: conditionTarget.search, totalCount: conditionTarget.totalCount }}
          />
        }
        bottomPadding="80px"
      >
        <StyledAccordion defaultExpanded={true}>
          <StyledAccordionSummary aria-controls={"proposal-input"} sx={{ height: 80, p: 0 }}>
            一括再提案 絞り込み条件
          </StyledAccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Box sx={{ mt: "10px", mr: "22px", mb: "25px", ml: "40px" }}>
              {/* 想定より多い対象に送ってしまわないよう、総件数を必ず出す */}
              <Typography variant={"h6"} sx={{ mb: 1 }}>
                対象: {conditionTarget.totalCount}名
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {conditionTarget.summaryParts.length > 0 ? conditionTarget.summaryParts.join(" / ") : "条件なし"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                ※本日すでに提案済みのユーザーは送信時に対象から除外されます。
              </Typography>
              {conditionTarget.totalCount === 0 && (
                <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                  条件に一致する提案対象のユーザーがいません。
                  <br />
                  提案済みのユーザー一覧で条件を指定し直してください。
                </Typography>
              )}
              {conditionTarget.totalCount > BULK_MAX_TARGET_USER_COUNT && (
                <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                  対象が上限の{BULK_MAX_TARGET_USER_COUNT.toLocaleString()}名を超えているため送信できません。
                  <br />
                  提案済みのユーザー一覧で条件を追加して対象を絞ってください。
                </Typography>
              )}
            </Box>
          </AccordionDetails>
        </StyledAccordion>
      </PageWithFixedBottom>
    );
  }

  return (
    <PageWithFixedBottom
      bottomContent={<CollapsibleInvestmentProposal userIds={eligibleUserIds} isBulkReProposal={true} />}
      bottomPadding="80px"
    >
      <StyledAccordion defaultExpanded={true}>
        <StyledAccordionSummary aria-controls={"proposal-input"} sx={{ height: 80, p: 0 }}>
          一括再提案 選択中ユーザID
        </StyledAccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Box sx={{ mt: "10px", mr: "22px", mb: "25px", ml: "40px" }}>
            {excludedTodayUserIds.length > 0 && (
              <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                本日すでに提案済みのユーザー{excludedTodayUserIds.length}名は提案対象外です。
              </Typography>
            )}
            {eligibleUserIds.length === 0 && selectedUserIds.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                提案対象のユーザーがいません。
                <br />
                提案済みのユーザーから、再度提案対象を選択してください。
              </Typography>
            )}
            {eligibleUserIds.length === 0 && selectedUserIds.length > 0 && (
              <Typography variant="body2" color="warning.main" marginBottom={1}>
                現在選択されているユーザーはすべて本日すでに提案済みのため、提案対象のユーザーがいません。
                <br />
                提案済みのユーザーから、再度提案対象を選択してください。
              </Typography>
            )}
            {selectedUserIds.map((userId) => (
              <Box key={userId} sx={{ mb: "12px" }}>
                <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                  <Tooltip title="提案対象から外す">
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveUserId(userId)}
                      aria-label={`ユーザーID ${userId} を提案対象から削除`}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Typography variant={"h6"}>ID: {userId}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    前回提案日時: {formatLastProposalText(userId)}
                  </Typography>
                  {isSameDayProposal(userId) && (
                    <Typography variant="body2" color="warning.main">
                      ※本日すでに提案済みのため提案対象外です
                    </Typography>
                  )}
                </Stack>
              </Box>
            ))}
          </Box>
        </AccordionDetails>
      </StyledAccordion>
    </PageWithFixedBottom>
  );
};

export default InvestmentProposalBulkReRegister;
